import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  ShoppingCart,
  Receipt,
  CardGiftcard,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getRevenueReport } from '../../services/api/partnerService';
import { RevenueReport, RevenueByProduct } from '../../types';

const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f'];

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
}

const SummaryCard = ({ title, value, subtitle, icon, color = '#1976d2' }: SummaryCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}15`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PartnerRevenuePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<RevenueReport | null>(null);

  // フィルター
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);

  useEffect(() => {
    loadReport();
  }, [year, month]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRevenueReport(year, month);
      setReport(data);
    } catch (err: any) {
      console.error('Report load error:', err);
      setError(err.response?.data?.detail || '売上レポートの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 年の選択肢を生成
  const years = [];
  for (let y = currentDate.getFullYear(); y >= currentDate.getFullYear() - 2; y--) {
    years.push(y);
  }

  // 月の選択肢
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadReport}>
          再読み込み
        </Button>
      </Box>
    );
  }

  if (!report) return null;

  const { summary, by_product, by_plan, monthly_trend } = report;

  // トレンドデータを整形
  const trendData = monthly_trend.map((item: { period: string; total_sales: number; partner_revenue: number }) => ({
    name: item.period,
    売上: item.total_sales,
    収益: item.partner_revenue,
  }));

  // 商品別データを整形
  const productData = by_product.map((item: RevenueByProduct) => ({
    name: item.product_name,
    value: item.total_sales,
  }));

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            売上レポート
          </Typography>
          <Typography variant="body2" color="text.secondary">
            売上と収益の詳細レポート
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>年</InputLabel>
            <Select value={year} label="年" onChange={(e) => setYear(e.target.value as number)}>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}年</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>月</InputLabel>
            <Select value={month} label="月" onChange={(e) => setMonth(e.target.value as number)}>
              {months.map((m) => (
                <MenuItem key={m} value={m}>{m}月</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="総売上"
            value={formatCurrency(summary.total_sales)}
            subtitle={`${summary.transaction_count}件の取引`}
            icon={<ShoppingCart sx={{ fontSize: 32, color: '#1976d2' }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="あなたの収益"
            value={formatCurrency(summary.partner_revenue)}
            icon={<AccountBalanceWallet sx={{ fontSize: 32, color: '#2e7d32' }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="プラットフォーム手数料"
            value={formatCurrency(summary.platform_fee)}
            icon={<Receipt sx={{ fontSize: 32, color: '#ed6c02' }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="無料登録"
            value={`${summary.free_registration_count}件`}
            icon={<CardGiftcard sx={{ fontSize: 32, color: '#9c27b0' }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* グラフ */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                月次推移
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `¥${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="売上" fill="#1976d2" />
                    <Bar dataKey="収益" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                商品別売上
              </Typography>
              {productData.length > 0 ? (
                <Box sx={{ height: 250 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={productData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(0)}%`}
                      >
                        {productData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                  <Typography color="text.secondary">データがありません</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 商品別・プラン別内訳 */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                商品別内訳
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>商品</TableCell>
                      <TableCell align="right">売上</TableCell>
                      <TableCell align="right">件数</TableCell>
                      <TableCell align="right">割合</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {by_product.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell align="right">{formatCurrency(item.total_sales)}</TableCell>
                        <TableCell align="right">{item.transaction_count}</TableCell>
                        <TableCell align="right">{item.percentage}%</TableCell>
                      </TableRow>
                    ))}
                    {by_product.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">データがありません</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                プラン別内訳
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>プラン</TableCell>
                      <TableCell align="right">売上</TableCell>
                      <TableCell align="right">件数</TableCell>
                      <TableCell align="right">割合</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {by_plan.map((item) => (
                      <TableRow key={item.plan_code}>
                        <TableCell>
                          <Chip label={item.plan_name} size="small" />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.total_sales)}</TableCell>
                        <TableCell align="right">{item.transaction_count}</TableCell>
                        <TableCell align="right">{item.percentage}%</TableCell>
                      </TableRow>
                    ))}
                    {by_plan.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="text.secondary">データがありません</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerRevenuePage;
